"use client";

import { useActionState, useEffect, useRef } from "react";

import Link from "next/link";

import { Button } from "@/components/button/button.component";
import { FieldErrorMessage } from "@/components/formError/fieldErrorMessage.component";
import { Text } from "@/components/text/text.component";

import {
  type EmailCaptureState,
  subscribeEmail,
} from "@/app/actions/subscribeEmail";
import { EVENTS } from "@/constants/analytics.constants";
import { FORM_EVENTS } from "@/constants/formAnalytics.constants";
import { useFormSubmitAttemptTag } from "@/hooks/useFormSubmitAttemptTag";
import useTagging from "@/hooks/useTagging";

export function FooterEmailForm({
  emailDisclaimer,
  emailSuccessMessage,
  emailPrivacyPolicyUrl,
}: FooterEmailFormProps) {
  const [state, formAction, isPending] = useActionState(
    subscribeEmail,
    INITIAL_STATE,
  );

  const inputRef = useRef<HTMLInputElement>(null);
  const errorId = "footer-email-error";
  const successId = "footer-email-success";
  const [, setTag] = useTagging();
  const prevStateRef = useRef(state);

  useFormSubmitAttemptTag(isPending, FORM_EVENTS.FOOTER_EMAIL.formEvent);

  useEffect(() => {
    if (prevStateRef.current === state) return;
    prevStateRef.current = state;

    if (state.status === "error") {
      inputRef.current?.focus();
      setTag({
        event: EVENTS.FORM_SUBMIT_FAILURE,
        formEvent: {
          ...FORM_EVENTS.FOOTER_EMAIL.formEvent,
          fieldName: "E_Mail_Address",
          errorCode: state.message,
        },
      });
    }

    if (state.status === "success") {
      setTag({
        event: EVENTS.FORM_SUBMIT_COMPLETE,
        outcome: "success",
        formEvent: FORM_EVENTS.FOOTER_EMAIL.formEvent,
      });
    }
  }, [state, setTag]);

  return (
    <div className="flex flex-col">
      <label htmlFor="footer-email-input">
        <Text variant="body2" className="text-white">
          Email address <span className="font-normal">(required)</span>
        </Text>
      </label>

      <form action={formAction} noValidate>
        <input type="hidden" name="source" value="Footer email" />
        <input
          type="hidden"
          name="successMessage"
          value={emailSuccessMessage ?? ""}
        />

        <div className="my-2 flex flex-row items-center gap-4">
          <input
            ref={inputRef}
            id="footer-email-input"
            name="email"
            type="email"
            required
            placeholder="Email address"
            autoComplete="email"
            aria-describedby={
              state.status === "error"
                ? errorId
                : state.status === "success"
                  ? successId
                  : undefined
            }
            aria-invalid={state.status === "error" ? "true" : undefined}
            className="peer bg-greyscale-background/10 placeholder-greyscale-background! focus:border-primary-200 flex-1 rounded-full border-2 border-white px-4 py-3 text-sm text-white transition-colors outline-none focus:ring-0"
            data-testid="footer-email"
          />
          <Button
            primary
            type="submit"
            disabled={isPending}
            aria-busy={isPending}
            className="shrink-0 px-6 py-4 peer-placeholder-shown:hidden">
            <Text variant="body3">Submit</Text>
          </Button>
        </div>

        {state.status === "error" && (
          <FieldErrorMessage
            id={errorId}
            className="mt-1 text-xs text-red-400"
            testId="footer-email-error">
            {state.message}
          </FieldErrorMessage>
        )}
      </form>

      {emailDisclaimer && (
        <Text
          variant="legal"
          className="text-white"
          data-testid="footer-email-disclaimer">
          {renderDisclaimer(emailDisclaimer, emailPrivacyPolicyUrl)}
        </Text>
      )}

      {state.status === "success" && (
        <p
          id={successId}
          role="status"
          aria-live="polite"
          className="mt-2 flex items-center gap-2 text-sm text-white"
          data-testid="footer-email-success">
          <span aria-hidden="true">&#10003;</span>
          {state.message}
        </p>
      )}
    </div>
  );
}

export type FooterEmailFormProps = {
  emailDisclaimer: string | null;
  emailSuccessMessage: string | null;
  emailPrivacyPolicyUrl: string | null;
};

const INITIAL_STATE: EmailCaptureState = { status: "idle" };

function renderDisclaimer(text: string, privacyUrl: string | null) {
  const SPLIT = "privacy policy";
  const idx = text.toLowerCase().indexOf(SPLIT);
  if (!privacyUrl || idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <Link
        href={privacyUrl}
        className="underline"
        target="_blank"
        rel="noopener noreferrer">
        {text.slice(idx, idx + SPLIT.length)}
      </Link>
      {text.slice(idx + SPLIT.length)}
    </>
  );
}
